import partition from 'lodash.partition';
import moment from 'moment';
import { RenderEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { isEmptyArrayOrObject, isEmptyObject, isFunction } from '@philips-internal/oswald-ui-utils';
import { INFORMATION_LIST_WIDTH, MAX_INTERVAL_SIZE, RELATIVE_TICK_POSITION, TimelineInterval } from './timeline-constants.js';
const Expressions = {
    TODAY: 'tws.time.today',
    YESTERDAY: 'tws.time.yesterday',
};
const DAY_MONTH_FORMAT = 'DD MMM';
const HOUR_FORMAT = 'LT';
const UNGROUPED_TITLE = 'ungrouped-time';
function getDynamicContent(options) {
    const { data, entry = '', onDynamicContentRender, type, value } = options;
    let content = value;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const defaultFunction = (_) => null;
    let renderFn = defaultFunction;
    const render = (fn) => {
        renderFn = fn;
    };
    if (onDynamicContentRender) {
        onDynamicContentRender(new RenderEvent({ data, entry, render, type }));
    }
    if (renderFn !== defaultFunction && isFunction(renderFn)) {
        content = renderFn(data);
    }
    return content;
}
function sortData(data) {
    const [eventsWithStartDate, eventsWithoutStartDate] = partition(data, (event) => event.start);
    const sortedEvents = eventsWithStartDate
        .sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf());
    return [...sortedEvents, ...eventsWithoutStartDate];
}
function getGroupedByTime(data, interval) {
    if (!data) {
        return null;
    }
    const groupedValue = {};
    const intervals = getIntervals(data, interval);
    const eventsWithoutTime = [];
    sortData(data).forEach((event) => {
        if (!event.start) {
            eventsWithoutTime.push(event);
            return;
        }
        const date = moment(event.start);
        const interval = intervals.find((interval, index) => ((date.isSameOrAfter(interval) && date.isBefore(intervals[index + 1]) || date.isSame(interval))));
        if (interval) {
            const oldGroupValue = groupedValue[interval.toString()] || [];
            oldGroupValue.push(event);
            groupedValue[interval.toString()] = oldGroupValue;
        }
    });
    if (!isEmptyArrayOrObject(eventsWithoutTime)) {
        groupedValue[UNGROUPED_TITLE] = eventsWithoutTime;
    }
    return groupedValue;
}
function getIntervals(events, interval) {
    const sortedEvents = sortData(events);
    if (interval === TimelineInterval.FIT) {
        return getFitInterval(sortedEvents);
    }
    const initialDate = moment(sortedEvents[0].start);
    const lastDate = getLastDate(sortedEvents);
    initialDate.set({ hour: initialDate.hours(), millisecond: 0, minute: 0, second: 0 });
    lastDate.set({ hour: lastDate.hours(), millisecond: 0, minute: 0, second: 0 });
    const intervals = [];
    intervals[0] = initialDate;
    while (intervals[intervals.length - 1] <= lastDate) {
        const lastInterval = intervals[intervals.length - 1];
        const newDateInterval = getIncreasedInterval(lastInterval, interval);
        intervals[intervals.length] = newDateInterval;
    }
    return intervals;
}
function getFitInterval(events) {
    return events.reduce((accumulator, event) => {
        if (event.start) {
            const interval = moment(event.start);
            if (!accumulator.some(oldInterval => oldInterval.isSame(interval))) {
                accumulator.push(interval);
            }
        }
        return accumulator;
    }, []);
}
function getLastDate(events) {
    const itemWithoutStart = events.find((event) => !event.start);
    return itemWithoutStart
        ? moment(events[events.indexOf(itemWithoutStart) - 1].start)
        : moment(events[events.length - 1].start);
}
function getIncreasedInterval(date, interval) {
    const newInterval = moment(date);
    // This code is ordered by time
    const setInterval = {
        [TimelineInterval.FIT]: () => newInterval,
        [TimelineInterval.FIFHTEEN_MINUTES]: () => newInterval.set({ minute: newInterval.minutes() + 15 }),
        [TimelineInterval.THIRTY_MINUTES]: () => newInterval.set({ minute: newInterval.minutes() + 30 }),
        [TimelineInterval.ONE_HOUR]: () => newInterval.set({ hour: newInterval.hours() + 1 }),
        [TimelineInterval.TWO_HOURS]: () => newInterval.set({ hour: newInterval.hours() + 2 }),
        [TimelineInterval.EIGHT_HOURS]: () => newInterval.set({ hour: newInterval.hours() + 8 }),
    };
    return setInterval[interval]();
}
function getGroupedData(data, groupBy) {
    if (!data || !Array.isArray(data) || !groupBy) {
        return null;
    }
    const groupedData = data.reduce((accumulator, timelineItem) => {
        const groupByFieldValue = String(timelineItem[groupBy]);
        if (!groupByFieldValue) {
            return accumulator;
        }
        const oldGroupValue = accumulator[groupByFieldValue] || [];
        oldGroupValue.push(timelineItem);
        accumulator[groupByFieldValue] = oldGroupValue;
        return accumulator;
    }, {});
    if (isEmptyObject(groupedData)) {
        return null;
    }
    return groupedData;
}
function getDateExpression(value) {
    const isToday = moment(value).isSame(moment(), 'day');
    const isYesterday = moment(value).isSame(moment().subtract(1, 'day'), 'day');
    if (isToday) {
        return Expressions.TODAY;
    }
    if (isYesterday) {
        return Expressions.YESTERDAY;
    }
    const date = moment(value);
    return date.isValid() ? date.format(DAY_MONTH_FORMAT) : '';
}
function getTimeExpression(value) {
    const date = moment(value);
    return date.isValid() ? date.local().format(HOUR_FORMAT) : '';
}
function getFormattedDate(date) {
    return { date: getDateExpression(date), time: getTimeExpression(date) };
}
function getFormattedActualDate() {
    const date = moment();
    return date.format(DAY_MONTH_FORMAT);
}
function getIntervalTime(interval) {
    // This code is ordered by time
    const intervals = {
        [TimelineInterval.FIT]: 0,
        [TimelineInterval.FIFHTEEN_MINUTES]: 15,
        [TimelineInterval.THIRTY_MINUTES]: 30,
        [TimelineInterval.ONE_HOUR]: 60,
        [TimelineInterval.TWO_HOURS]: 120,
        [TimelineInterval.EIGHT_HOURS]: 480,
    };
    return intervals[interval];
}
function getIntervalsValue() {
    return [
        TimelineInterval.FIT,
        TimelineInterval.FIFHTEEN_MINUTES,
        TimelineInterval.THIRTY_MINUTES,
        TimelineInterval.ONE_HOUR,
        TimelineInterval.TWO_HOURS,
        TimelineInterval.EIGHT_HOURS,
    ];
}
function getSuperscription(value, superscription = []) {
    return superscription.find((item) => item.value === String(value));
}
function getSchedulerWidth(intervals) {
    return MAX_INTERVAL_SIZE * intervals;
}
function getTimelineWidth(intervals) {
    return getSchedulerWidth(intervals) + INFORMATION_LIST_WIDTH + RELATIVE_TICK_POSITION;
}
export { getDynamicContent, getFormattedDate, getFormattedActualDate, getGroupedByTime, getGroupedData, getIntervals, getIntervalTime, getIntervalsValue, getSchedulerWidth, getSuperscription, getTimeExpression, getTimelineWidth, };
//# sourceMappingURL=timeline-utils.js.map